<template>
  <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
    <div class="row" style="border-bottom: 1px solid #D8D8D8">
      <div class="col-lg-9 col-md-12 col-12">
        <SelectVue
            v-model="formData.priceType"
            :options="props.meetingTypeDropdown"
            @update:modelValue="onUpdateMeetingType"
            :required="true"
            label="Preisart"
            :validation="v$.priceType"
            :messages="v$.priceType.required.$message"/>
      </div>
      <div class="col-lg-3 col-md-12 col-12">
        <Datepicker
            v-model="formData.startFrom"
            :required="true"
            :label="$t('general.start_from')"
            position="down"
            :validation="v$.startFrom"
            :messages="v$.startFrom.required.$message"/>
      </div>
    </div>

    <div class="row mt-3">
      <div class="col-lg-9 col-md-12 col-sm-12">
        <BaseInput
            v-model="formData.priceDescription"
            label="Bezeichnung"
            :required=true
            :validation="v$.priceDescription"
            :messages="v$.priceDescription.required.$message"/>
      </div>
      <div class="col-lg-3 col-md-12 col-12">
        <BaseNumber
            @update:modelValue="onUpdatePrice"
            v-model="formData.price"
            :label="$t('general.price')"
            :required=true
            :min="-1000000"
            :decimalPlaces="2"
            :validation="v$.price"
            :messages="v$.price.required.$message"/>
      </div>
    </div>

    <div class="row" v-if="showHalfPriceDescription">
      <div class="col-lg-9 col-md-12 col-sm-12">
        <BaseInput
            v-model="formData.halfPriceDescription"
            label="Beschreibung der halben Einheit"
            :required=true
            :validation="v$?.halfUnitDescription"
            :messages="v$?.halfUnitDescription?.required.$message"/>
      </div>
      <div class="col-lg-3 col-md-12 col-12">
        <BaseNumber
            disabled="disabled"
            v-model="formData.halfPrice"
            label="halber Preis"
            :required=true
            :min="-1000000"
            :decimalPlaces="2"/>
      </div>
    </div>

    <div class="row" v-if="showTenMinutesPriceDescription">
      <div class="col-lg-9 col-md-12 col-sm-12">
        <BaseInput
            v-model="formData.tenMinutesPriceDescription"
            label="Zehn Minuten Beschreibung"
            :required=true
            :validation="v$?.tenMinutesPriceDescription"
            :messages="v$?.tenMinutesPriceDescription?.required.$message"/>
      </div>
      <div class="col-lg-3 col-md-12 col-12">
        <BaseNumber
            disabled="disabled"
            v-model="formData.tenMinutesPrice"
            label="Zehn-Minuten-Preis"
            :required=true
            :min="-1000000"
            :decimalPlaces="2"/>
      </div>
    </div>

    <div class="d-flex justify-content-center w-100 mt-3">
      <SaveButton
          :label="$t('general.button.save')"
          :loading="isSubmitting"/>
    </div>
  </form>
</template>

<script setup lang="ts">
import {ErrorToast, SuccessToast} from '@/components/VueToast';
import {computed, defineEmits, defineProps, nextTick, onMounted, PropType, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import coursePriceCoachingService from "@/api/Course/SocioTherapyCoaching";
import Datepicker from "@/components/picker/Datepicker.vue";
import BaseNumber from "@/components/base/BaseNumber.vue";
import useVuelidate from "@vuelidate/core";
import SelectVue from "@/components/SelectVue.vue";
import BaseInput from "@/components/base/BaseInput.vue";
import {IFormRules, IPrice} from "@/interfaces/Course/SocioTherapy/ICoursePrice";
import {requiredRule} from "@/shared/validators/validationRules";
import SaveButton from "@/components/button/SaveButton.vue";

const {t} = useI18n();
const isLoaded = ref(false);
const isSubmitting = ref(false);
const emit = defineEmits(['update:modelValue', 'update:submitForm']);
const showHalfPriceDescription = ref(false)
const showTenMinutesPriceDescription = ref(false)

const props = defineProps({
  priceId: {
    type: Number as PropType<number | null>,
    required: false,
    default: null
  },
  meetingTypeDropdown: {
    type: Array,
    required: true,
  },
  submitForm: {
    type: Boolean,
    required: true,
    default: false,
  },
});

const formData = ref<IPrice>({
  operationType: 'sociotherapie',
  priceType: '',
  startFrom: '',
  price: "",
  halfPrice: "",
  tenMinutesPrice: "",
  priceDescription: '',
  halfPriceDescription: '',
  tenMinutesPriceDescription: '',
});

const formRules = ref<IFormRules>({
  operationType: requiredRule(t),
  priceType: requiredRule(t),
  priceDescription: requiredRule(t),
  startFrom: requiredRule(t),
  price: requiredRule(t),
});

const computedRules = computed(() => formRules.value);
const v$ = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {
    formData.value.price = formData.value.price ? Number(formData.value.price) : null;
    formData.value.halfPrice = formData.value.halfPrice ? Number(formData.value.halfPrice) : null;
    formData.value.tenMinutesPrice = formData.value.tenMinutesPrice ? Number(formData.value.tenMinutesPrice) : null;

    if (props.priceId) {
      await coursePriceCoachingService.putPrice(formData.value, +props.priceId);
    } else {
      await coursePriceCoachingService.postPrice(formData.value);
    }

    emit('update:modelValue', false);
    emit('update:submitForm', !props.submitForm);
    SuccessToast(t('general.toasts.save_success'));
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getPriceData(priceId: number) {
  try {
    const {data} = await coursePriceCoachingService.getPrice(+priceId);
    const priceData = data.data;

    formData.value = {
      operationType: priceData.operationType,
      priceType: priceData.priceType,
      startFrom: priceData.startFrom,
      price: priceData.price,
      halfPrice: priceData.halfPrice,
      tenMinutesPrice: priceData.tenMinutesPrice,
      priceDescription: priceData.priceDescription,
      halfPriceDescription: priceData.halfPriceDescription,
      tenMinutesPriceDescription: priceData.tenMinutesPriceDescription
    };

    onUpdateMeetingType(data.data.priceType, true)
  } catch (error) {
    await ErrorToast(error);
  }
}

function onUpdateMeetingType(value: string, onFormUpdate: boolean = false) {
  if (!onFormUpdate) {
    formData.value.price = '';
    formData.value.halfPrice = '';
    formData.value.tenMinutesPrice = ''
  }

  if (value == "sociotherapy" || value == "initial_consultation") {
    formRules.value = {
      ...formRules.value,
      halfPriceDescription: requiredRule(t),
      tenMinutesPriceDescription: requiredRule(t),
    };

    showHalfPriceDescription.value = true
    showTenMinutesPriceDescription.value = true
  } else {
    formRules.value = {
      ...formRules.value,
      halfPriceDescription: "",
      tenMinutesPriceDescription: ""
    };

    showHalfPriceDescription.value = false
    showTenMinutesPriceDescription.value = false
  }
}

function onUpdatePrice() {
  nextTick(() => {
    formData.value.halfPrice = Math.round((Number(formData.value.price!) / 2) * 100) / 100;
    formData.value.tenMinutesPrice = Math.round((Number(formData.value.price!) / 60 * 10) * 100) / 100;
  });
}


onMounted(async () => {
  if (props.priceId) {
    await getPriceData(props.priceId);
  }

  isLoaded.value = true;
});
</script>
<style scoped>

</style>